import React, { useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { showAlert } from '../../../redux/alert/alertActions';
import { showModal, hideModal } from '../../../redux/modal/modalActions';

import { formatDate } from '../../../utils/timeUtils';
import { votePost } from '../../../services/postService';

import Icon from '../../Icon/Icon';
import PulsatingIcon from '../../Icon/PulsatingIcon/PulsatingIcon';
import LoginCard from '../../LoginCard/LoginCard';

const detectStandalone = () => {
  if (typeof window === 'undefined') return false;
  const mediaQuery = window.matchMedia?.('(display-mode: standalone)');
  const matchesMedia = !!mediaQuery && mediaQuery.matches;
  const navigatorStandalone =
    typeof window.navigator !== 'undefined' && window.navigator.standalone;
  return Boolean(matchesMedia || navigatorStandalone);
};

const PostDialogStats = ({
  currentUser,
  post,
  token,
  dispatch,
  profileDispatch,
  showAlert,
  showModal,
  hideModal,
  simple,
  onCommentClick,
  postNote,
  isLocked = false,
  commentBox,
}) => {
  const history = useHistory();
  const ref = useRef();
  const postVotes = Array.isArray(post.postVotes) ? post.postVotes : [];
  const postId = post._id || post.postId;
  const aggregateReactions =
    (post.likeCount || 0) + (post.loveCount || 0) + (post.hahaCount || 0);
  const votesCount =
    typeof post.postVotesCount === 'number'
      ? post.postVotesCount
      : postVotes.length;
  const totalLikesDisplayed =
    aggregateReactions > 0 ? aggregateReactions : votesCount;
  const totalComments =
    typeof post.commentCount === 'number'
      ? post.commentCount
      : typeof post.commentData?.commentCount === 'number'
      ? post.commentData.commentCount
      : typeof post.commentData?.totalCount === 'number'
      ? post.commentData.totalCount
      : Array.isArray(post.commentData?.comments)
      ? post.commentData.comments.length
      : Array.isArray(post.comments)
      ? post.comments.length
      : undefined;

  const handleClick = async () => {
    if (isLocked) {
      showAlert('Unlock this post to react.');
      return;
    }
    if (!currentUser) {
      return showModal(
        {
          children: <LoginCard onClick={() => hideModal('Card/Card')} modal />,
          style: {
            gridColumn: 'center-start / center-end',
            justifySelf: 'center',
            width: '40rem',
          },
        },
        'Card/Card'
      );
    }
    const alreadyLiked =
      postVotes.some((vote) => vote.author === currentUser._id) ||
      !!post.reactionType;
    // Dispatch the action immediately for optimistic UI
    dispatch({
      type: 'VOTE_POST',
      payload: {
        currentUser,
        postId,
        dispatch: profileDispatch,
      },
    });
    try {
      if (alreadyLiked) {
        const { removePostReaction } = await import('../../../services/postService');
        await removePostReaction(postId, token);
      } else {
        await votePost(postId, token);
      }
    } catch (err) {
      showAlert('Could not vote on the post.', () => handleClick());
    }
  };

  const isStandalone = useMemo(detectStandalone, []);

  const postDialogStatsClassNames = classNames({
    'post-dialog__stats': true,
    'post-dialog__stats--simple': simple,
    'post-dialog__stats--pwa': simple && isStandalone,
  });

  const handleCommentCountClick = useCallback(
    (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      if (postId) {
        history.push(`/post/${postId}`);
      }
    },
    [history, postId]
  );

  const focusCommentInput = () => {
    if (!currentUser || !ref.current) {
      return;
    }
    const commentForm = ref.current.nextElementSibling;
    const input = commentForm?.querySelector('input.add-comment__input');
    if (input) {
      input.focus();
    }
  };

  const handleCommentIconClick = (event) => {
    if (onCommentClick) {
      onCommentClick(event);
      return;
    }
    focusCommentInput();
  };

  const actionsMarkup = isLocked ? (
    <div className="post-dialog__actions post-dialog__actions--locked">
      <Icon icon="lock-closed" className="icon--button" />
      <span className="post-dialog__locked-note">Unlock to react</span>
    </div>
  ) : (
    <div className="post-dialog__actions">
      {currentUser ? (
        <PulsatingIcon
          toggle={
            postVotes.some((vote) => vote.author === currentUser._id) ||
            !!post.reactionType
          }
          elementRef={ref}
          constantProps={{
            onClick: () => handleClick(),
          }}
          toggledProps={[
            {
              className: 'icon--button post-dialog__like color-red',
              icon: 'heart',
            },
            {
              className: 'icon--button post-dialog__like',
              icon: 'heart-outline',
            },
          ]}
        />
      ) : (
        <Icon
          onClick={() => handleClick()}
          icon="heart-outline"
          className="icon--button post-dialog__like"
        />
      )}
      <Icon
        onClick={handleCommentIconClick}
        className="icon--button"
        icon="chatbubble-outline"
      />
      <Icon className="icon--button" icon="paper-plane-outline" />
    </div>
  );

  const likesMarkup = (
    <p className="heading-4 post-dialog__likes">
      {totalLikesDisplayed === 0 ? (
        <span>
          Be the first to{' '}
          <b
            style={{ cursor: 'pointer' }}
            onClick={(event) => {
              event.nativeEvent.stopImmediatePropagation();
              handleClick();
            }}
            data-test="component-like-button"
          >
            like this
          </b>
        </span>
      ) : (
        <span>
          <b>
            {totalLikesDisplayed}{' '}
            {totalLikesDisplayed === 1 ? 'like' : 'likes'}
          </b>
        </span>
      )}
      {typeof totalComments === 'number' && (
        <span
          className="post-dialog__comment-count"
          role="button"
          tabIndex={0}
          onClick={handleCommentCountClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              handleCommentCountClick(event);
            }
          }}
        >
          {' '}
          |{' '}
          <b aria-label="View all comments">
            {totalComments}{' '}
            {totalComments === 1 ? 'comment' : 'comments'}
          </b>
        </span>
      )}
    </p>
  );

  const dateMarkup = (
    <p className="heading-5 post-dialog__date color-light uppercase">
      {formatDate(post.date)}
    </p>
  );

  return (
    <div
      ref={ref}
      className={postDialogStatsClassNames}
      data-test="component-post-dialog-stats"
      style={
        simple && isStandalone
          ? { paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 6rem)' }
          : undefined
      }
    >
      {simple ? (
        <>
          <div className="post-dialog__stats-row">
            <div className="post-dialog__meta">
              {likesMarkup}
              {dateMarkup}
              {postNote && <p className="post-dialog__note">{postNote}</p>}
            </div>
            <div className="post-dialog__actions-block">
              {actionsMarkup}
            </div>
          </div>
          {commentBox && (
            <div className="post-dialog__inline-comment">{commentBox}</div>
          )}
        </>
      ) : (
        <>
          {actionsMarkup}
          {commentBox}
          {postNote && <p className="post-dialog__note">{postNote}</p>}
          {likesMarkup}
          {dateMarkup}
        </>
      )}
    </div>
  );
};

PostDialogStats.propTypes = {
  currentUser: PropTypes.object,
  post: PropTypes.object.isRequired,
  token: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  profileDispatch: PropTypes.func,
  onCommentClick: PropTypes.func,
  postNote: PropTypes.string,
  isLocked: PropTypes.bool,
  commentBox: PropTypes.node,
};

const mapDispatchToProps = (dispatch) => ({
  showAlert: (text, onClick) => dispatch(showAlert(text, onClick)),
  showModal: (props, component) => dispatch(showModal(props, component)),
  hideModal: (component) => dispatch(hideModal(component)),
});

export default connect(null, mapDispatchToProps)(PostDialogStats);
